/**
 * scroll-tab-switch-v2-script.js
 *
 * Custom element <scroll-tab-switch-v2>
 *
 * Features:
 *  - IntersectionObserver hides the site header when this section is in view,
 *    so the sticky tab nav sits flush at top:0 with zero spacing.
 *  - Scroll-spy: highlights the correct tab as the user scrolls through panels.
 *  - Tab click: smooth cubic-ease scroll to the target panel.
 *  - Horizontal tab-strip auto-scroll: keeps the active chip centred.
 *  - Resize: recalculates offsets.
 *
 * CWV notes:
 *  - Header hide/show uses IntersectionObserver (zero scroll listeners, zero
 *    forced layout reads). The hide is a CSS transform — compositor only.
 *  - Scroll-spy is rAF-throttled to one read per frame.
 *  - No layout reads inside event handlers (all reads are batched in rAF).
 *
 * Body class contract:
 *  - body.sts2-nav-active → header hidden, .sts2-nav top:0
 *  - CSS in scroll-tab-switch-v2-stylesheet.css handles the visual response.
 */

if (!customElements.get('scroll-tab-switch-v2')) {
  class ScrollTabSwitchV2 extends HTMLElement {
    constructor() {
      super();

      this._onScroll    = this._onScroll.bind(this);
      this._onResize    = this._onResize.bind(this);
      this._onTabClick  = this._onTabClick.bind(this);
      this._onNavScroll = this._onNavScroll.bind(this);

      this._isProgrammaticScroll  = false;
      this._programmaticScrollTimer = null;
      this._rafId               = null;
      this._navFadeRafId        = null;
      this._scrollAnimationId   = null;

      // IntersectionObserver for header hide/show
      this._sectionObserver = null;
    }

    /* ------------------------------------------------------------------
       Lifecycle
    ------------------------------------------------------------------ */

    connectedCallback() {
      this._nav        = this.querySelector('.sts2-nav');
      this._navScroller = this.querySelector('.sts2-nav__scroll');
      this._tabs   = Array.from(this.querySelectorAll('.sts2-tab'));
      this._panels = Array.from(this.querySelectorAll('.sts2-panel'));

      if (!this._tabs.length || !this._panels.length) return;

      this._tabs.forEach(tab => tab.addEventListener('click', this._onTabClick));
      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onResize, { passive: true });
      if (this._navScroller) {
        this._navScroller.addEventListener('scroll', this._onNavScroll, { passive: true });
      }

      // Start observing for header-hide behaviour
      this._initSectionObserver();

      requestAnimationFrame(() => {
        this._updateActive();
        this._updateScrollFades();
      });
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onResize);
      if (this._navScroller) {
        this._navScroller.removeEventListener('scroll', this._onNavScroll);
      }

      if (this._programmaticScrollTimer) clearTimeout(this._programmaticScrollTimer);
      if (this._scrollAnimationId)       cancelAnimationFrame(this._scrollAnimationId);
      if (this._rafId)                   cancelAnimationFrame(this._rafId);
      if (this._navFadeRafId)            cancelAnimationFrame(this._navFadeRafId);

      if (this._sectionObserver) {
        this._sectionObserver.disconnect();
        this._sectionObserver = null;
      }

      // Always restore header when element is removed
      document.body.classList.remove('sts2-nav-active');
    }

    /* ------------------------------------------------------------------
       HEADER HIDE via IntersectionObserver
       ─────────────────────────────────────────────────────────────────
       We observe the <scroll-tab-switch-v2> element itself.

       threshold: 0 → callback fires as soon as even 1px enters/leaves
       the viewport. Combined with rootMargin we can fire slightly BEFORE
       the section reaches the top so the header is already hidden by the
       time the tab nav would need to take its place.

       rootMargin: '-headerHeight px 0px 0px 0px' would be ideal but we
       don't know the header height at parse time. We use 0px and handle
       the small overlap in CSS with a transition.

       isIntersecting = true  → section is visible → hide header
       isIntersecting = false → section not visible → show header
    ------------------------------------------------------------------ */

    _initSectionObserver() {
      // Only run if IntersectionObserver is supported (all modern browsers)
      if (!('IntersectionObserver' in window)) return;

      this._sectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              // Section entered viewport — hide header, nav goes to top:0
              document.body.classList.add('sts2-nav-active');
            } else {
              // Section left viewport — restore header
              document.body.classList.remove('sts2-nav-active');
            }
          });
        },
        {
          // threshold:0 fires as soon as 1px of the section is visible
          threshold: 0,
          // No rootMargin — fire exactly when section hits viewport edge
          rootMargin: '0px 0px 0px 0px'
        }
      );

      this._sectionObserver.observe(this);
    }

    /* ------------------------------------------------------------------
       Offset helpers
       When sts2-nav-active is set, the header is hidden so header offset
       is effectively 0. We read the nav height directly.
    ------------------------------------------------------------------ */

    _getHeaderOffset() {
      // If we've hidden the header, it contributes 0 to offset
      if (document.body.classList.contains('sts2-nav-active')) return 0;

      const styles = getComputedStyle(document.documentElement);
      return (
        parseFloat(styles.getPropertyValue('--sticky-header-height')) ||
        parseFloat(styles.getPropertyValue('--header-height')) ||
        0
      );
    }

    _getStickyOffset() {
      const navH = this._nav ? this._nav.offsetHeight : 0;
      return this._getHeaderOffset() + navH + 8;
    }

    /* ------------------------------------------------------------------
       Tab click → smooth scroll to panel
    ------------------------------------------------------------------ */

    _onTabClick(event) {
      const index = parseInt(event.currentTarget.dataset.index, 10);
      if (Number.isNaN(index)) return;
      this._scrollToPanel(index);
    }

    _scrollToPanel(index) {
      const panel = this._panels[index];
      if (!panel) return;

      const offset  = this._getStickyOffset();
      const targetY = panel.getBoundingClientRect().top + window.scrollY - offset;
      const duration = 800;

      this._isProgrammaticScroll = true;
      this._setActive(index);

      if (this._programmaticScrollTimer) clearTimeout(this._programmaticScrollTimer);
      this._programmaticScrollTimer = setTimeout(() => {
        this._isProgrammaticScroll = false;
      }, duration + 120);

      this._animateScrollTo(targetY, duration);
    }

    /* ------------------------------------------------------------------
       Cubic-ease scroll animation
    ------------------------------------------------------------------ */

    _animateScrollTo(targetY, duration) {
      if (this._scrollAnimationId) cancelAnimationFrame(this._scrollAnimationId);

      const startY   = window.scrollY;
      const distance = targetY - startY;
      if (Math.abs(distance) < 1) return;

      const startTime = performance.now();
      const ease = t =>
        t < 0.5
          ? 4 * t * t * t
          : 1 - Math.pow(-2 * t + 2, 3) / 2;

      const step = now => {
        const progress = Math.min((now - startTime) / duration, 1);
        window.scrollTo(0, startY + distance * ease(progress));
        if (progress < 1) {
          this._scrollAnimationId = requestAnimationFrame(step);
        } else {
          this._scrollAnimationId = null;
        }
      };

      this._scrollAnimationId = requestAnimationFrame(step);
    }

    /* ------------------------------------------------------------------
       Scroll / resize → rAF-throttled
    ------------------------------------------------------------------ */

    _onScroll() {
      if (this._rafId) return;
      this._rafId = requestAnimationFrame(() => {
        this._rafId = null;
        if (!this._isProgrammaticScroll) this._updateActive();
      });
    }

    _onResize() {
      if (this._rafId) return;
      this._rafId = requestAnimationFrame(() => {
        this._rafId = null;
        this._updateActive();
        this._updateScrollFades();
      });
    }

    /* ------------------------------------------------------------------
       Horizontal tab-strip scroll → edge fade masks
       Fires on the strip's own scroll (drag/swipe), separate from the
       page-level _onScroll above (window scrolling and dragging the tab
       strip sideways are different scroll events on different elements).
    ------------------------------------------------------------------ */

    _onNavScroll() {
      if (this._navFadeRafId) return;
      this._navFadeRafId = requestAnimationFrame(() => {
        this._navFadeRafId = null;
        this._updateScrollFades();
      });
    }

    _updateScrollFades() {
      const scroller = this._navScroller;
      if (!scroller) return;

      const maxScroll = scroller.scrollWidth - scroller.clientWidth;
      const hasOverflow = maxScroll > 1;
      const atStart = scroller.scrollLeft <= 1;
      const atEnd   = scroller.scrollLeft >= maxScroll - 1;

      scroller.classList.toggle('sts2-nav__scroll--fade-left', hasOverflow && !atStart);
      scroller.classList.toggle('sts2-nav__scroll--fade-right', hasOverflow && !atEnd);
    }

    /* ------------------------------------------------------------------
       Scroll-spy
    ------------------------------------------------------------------ */

    _updateActive() {
      const triggerLine = this._getStickyOffset() + 24;
      let activeIndex = 0;

      for (let i = 0; i < this._panels.length; i++) {
        if (this._panels[i].getBoundingClientRect().top - triggerLine <= 0) {
          activeIndex = i;
        } else {
          break;
        }
      }

      this._setActive(activeIndex);
    }

    /* ------------------------------------------------------------------
       Set active tab + centre it in the horizontal scroll strip
    ------------------------------------------------------------------ */

    _setActive(index) {
      this._tabs.forEach((tab, i) => {
        const isActive = i === index;
        tab.classList.toggle('sts2-tab--active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      this._scrollTabIntoView(index);
    }

    _scrollTabIntoView(index) {
      const tab      = this._tabs[index];
      const scroller = this.querySelector('.sts2-nav__scroll');
      if (!tab || !scroller) return;

      const tabRect      = tab.getBoundingClientRect();
      const scrollerRect = scroller.getBoundingClientRect();

      if (tabRect.left < scrollerRect.left || tabRect.right > scrollerRect.right) {
        const target = tab.offsetLeft - scroller.clientWidth / 2 + tab.offsetWidth / 2;
        scroller.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
      }
    }
  }

  customElements.define('scroll-tab-switch-v2', ScrollTabSwitchV2);
}